<script setup>
import { onBeforeMount } from 'vue'
import { numberWithCommas } from '@/plugins/misc'
import { useUsersStore, useUserGroupsStore, useReservationsStore, useRestaurantsStore } from '@/stores'
import stanchion from '@/assets/stanchion-rope-stands-green.svg'

const selectedMarketHappenings = ref('')

const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()

const {
  happeningsRestaurants,
  restaurants,
  viewingDetailsRestaurant,
  viewingDetailsRestaurantTab
} = storeToRefs(restaurantsStore)
const { myUser } = storeToRefs(usersStore)

onBeforeMount(async () => {
  await restaurantsStore.getHappeningsRestaurants()
})

const newDraft = async (market, restaurant) => {
  await usersStore.showLoadingOverlay()
  try {
    if (!myUser.value) {
      await usersStore.loginNewDummyUser()
    }
    await usersStore.startNewReservation({ restaurant, market })
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

const showRestaurantSpaces = (marketName, restaurantName) => {
  // find the restaurant
  let restaurant = restaurants.value.find(r => r.name == restaurantName && r.market == marketName)
  if (restaurant) {
    viewingDetailsRestaurantTab.value = 'Events'
    viewingDetailsRestaurant.value = restaurant
  }
}

const showRestaurantHappenings = (marketName, restaurantName) => {
  let restaurant = restaurants.value.find(r => (r.name == restaurantName && r.market == marketName) || (r.confirmedRestaurant == restaurantName && r.market == marketName))
  if (restaurant) {
    viewingDetailsRestaurantTab.value = 'Happenings'
    viewingDetailsRestaurant.value = restaurant
  }
}

const selectableMarketsHappenings = computed(() => {
  let marketsCount = {}
  let returnArray = happeningsRestaurants.value.filter(restaurant => {
    if (restaurant.showHappeningsIcon) {
      marketsCount[restaurant.market] = marketsCount[restaurant.market] || { count: 0 }
      marketsCount[restaurant.market].count++
      
      return true
    }
    
    return false
  })

  const totalCount = returnArray.length

  returnArray = Object.keys(marketsCount).map(market => ({
    text: `${market} (${numberWithCommas(marketsCount[market])})`,
    value: market,
    count: marketsCount,
  }))

  let sortedMarkets = Object.keys(marketsCount).sort((a, b) => {
    if (marketsCount[a].count === marketsCount[b].count) {
      return a.localeCompare(b)
    }
    
    return marketsCount[b].count - marketsCount[a].count
  })

  returnArray = sortedMarkets.map(market => {
    return {
      text: `${market} (${numberWithCommas(marketsCount[market].count)})`,
      value: market,
    }
  })

  returnArray.unshift({ text: `All Cities (${numberWithCommas(totalCount)} Favorites)`, value: '' })

  return returnArray
})

const filteredHappenings = computed(() => {
  if (happeningsRestaurants.value) {
    var returnArray = happeningsRestaurants.value.filter(restaurant => restaurant.showHappeningsIcon === true)

    if (selectedMarketHappenings.value && selectedMarketHappenings.value != null && returnArray) {
      returnArray = returnArray.filter(item => {
        return item.market == selectedMarketHappenings.value
      })
    }
    
    return returnArray
  } else {
    return []
  }
})

const backHome = computed(() => {
  if (myUser?.value && myUser?.value?.userLevel != 'dummy') {
    return '/discover'
  }
  
  return '/home/'
})

const showRestaurantDetails = (marketName, restaurantName) => {
  // find the restaurant
  let restaurant = restaurants.value.find(r => r.name == restaurantName && r.market == marketName)
  if (restaurant) {
    viewingDetailsRestaurantTab.value = 'Details'
    viewingDetailsRestaurant.value = restaurant
  }
}
</script>

<template>
  <VContainer fluid>
    <div
      class="d-flex flex-column"
      :class="{
        'justify-center align-start': !$vuetify.display.smAndDown,
        'justify-center align-center': $vuetify.display.smAndDown
      }"
    >
      <div
        v-if="!$vuetify.display.smAndDown"
        class="d-flex align-center mb-4 w-100"
      >
        <RouterLink :to="backHome">
          <VIcon
            color="secondary"
            size="x-large"
            class="mr-4"
          >
            mdi-chevron-left
          </VIcon>
        </RouterLink>
        <h1 class="text-center text-lg">
          Happenings
        </h1>
      </div>
      <div
        v-else
        class="d-flex justify-center align-center mb-4 w-100"
      >
        <span class="dark-text d-flex align-center">
          <VIcon
            size="xs"
            class="mt-0"
            style="margin-left: -3px;"
          >
            emojione-v1:lightning-mood
          </VIcon>
          <span style="margin-left: -2px; margin-right: 4px;">: </span>
          <span class="text-caption">
            Restaurants with Experiences, Offers & Updates live
          </span>
        </span>
      </div>
      <VAutocomplete
        v-model="selectedMarketHappenings"
        label="City"
        :messages="!$vuetify.display.smAndDown ? 'Select a city' : ''"
        :items="selectableMarketsHappenings"
        :menu-props="{ density: 'compact', height: '100%', width: !$vuetify.display.smAndDown ? 'auto' : '50%', minHeight: '50%' }"
        item-title="text"
        item-value="value"
        no-data-text="No Matches"
        clearable
        clear-on-select
        density="compact"
        :width="!$vuetify.display.smAndDown ? '50%' : '100%'"
      >
        <template
          v-if="!$vuetify.display.smAndDown"
          #message="{ message }"
        >
          <div class="dark-text d-flex align-center">
            <VIcon
              class="mt-0"
              style="margin-left: -3px;"
            >
              emojione-v1:lightning-mood
            </VIcon>
            <span style="margin-left: -2px; margin-right: 4px;">: </span>
            <span>
              Restaurants with Experiences, Offers & Updates live
            </span>
          </div>
        </template>
      </VAutocomplete>
    </div>
    <VRow v-if="!$vuetify.display.smAndDown">
      <VVirtualScroll
        :items="filteredHappenings"
        max-height="600"
        item-height="98"
      >
        <template #default="{ item, index }">
          <VListItem @click="showRestaurantDetails(item.market, item.name)">
            <div class="d-flex flex-row justify-start align-center">
              <div class="restaurant-image-holder--mobile flex-shrink-0">
                <GoogleImage
                  class="restaurant-image"
                  :restaurant="item"
                />
              </div>
              <div class="pa-4">
                <VListItemTitle>
                  <div class="d-flex flex-wrap align-items-center">
                    <div>
                      <VIcon
                        v-if="item.status === 'Preferred'"
                        style="color: rgb(var(--v-theme-primary));"
                      >
                        iconamoon:certificate-badge-light
                      </VIcon>
                      <span
                        class="font-weight-bold text-truncate"
                        :style="(item.status || item.raw.status) === 'Preferred' ? 'color: rgb(var(--v-theme-primary));' : 'margin-left: 24px;'"
                      >{{ item.name }}</span>
                      <span class="trending-name mt-1 ml-1">
                        <RestaurantHeart
                          :restaurant-name="item.name"
                          :restaurant-market="item.market"
                        />
                      </span>
                    </div>
                  </div>
                </VListItemTitle>
                <VListItemSubtitle
                  :style="(item.status || item.raw.status) === 'Preferred' ? 'margin-left: 24px; color: black;' : 'color: black; margin-left: 24px;'"
                >
                  <span
                    v-if="item.group"
                    class="trending-company text-xs"
                  >{{ item.group }}</span>
                  <span class="trending-company text-xs">{{ item.market }}</span>
                  <div class="d-flex align-center">
                    <div
                      class="d-flex cursor-pointer"
                      @click.stop="showRestaurantSpaces(item.market ?? item.raw.market, item.name ?? item.raw.name)"
                    >
                      <img
                        v-if="item?.eventSpaces?.length > 0"
                        :src="stanchion"
                        height="16"
                        class="align-self-center"
                      >
                      <span
                        v-if="item?.eventSpaces?.length > 0"
                        class="text-xs ml-1"
                      >
                        {{ item?.eventSpaces?.length }}
                      </span>
                    </div>
                    <VIcon
                      v-if="item?.showHappeningsIcon"
                      size="16"
                      class="align-self-center cursor-pointer"
                      @click.stop="showRestaurantHappenings(item.market ?? item.raw.market, item.name ?? item.raw.name)"
                    >
                      emojione-v1:lightning-mood
                    </VIcon>
                  </div>
                </VListItemSubtitle>
              </div>
            </div>
            <template #append>
              <VBtn
                size="small"
                density="compact"
                color="primary"
                style="padding-left: 5px !important; padding-right: 5px !important;"
                icon="mdi-plus"
                @click.stop="newDraft(item.market, item.name);"
              />
            </template>
          </VListItem>
          <VDivider inset />
        </template>
      </VVirtualScroll>
    </VRow>
    <VRow v-else>
      <VCol
        cols="12"
        class="trending-wrapper"
      >
        <VVirtualScroll
          :items="filteredHappenings"
          max-height="600"
          item-height="98"
        >
          <template #default="{ item, index }">
            <VListItem @click="showRestaurantDetails(item.market, item.name)">
              <div class="d-flex flex-row justify-start align-center">
                <div class="restaurant-image-holder--mobile flex-shrink-0">
                  <GoogleImage
                    class="restaurant-image"
                    :restaurant="item"
                  />
                </div>
                <div class="py-4 pl-4">
                  <VListItemTitle>
                    <div class="d-flex flex-wrap justify-start align-items-center">
                      <div class="d-flex align-start">
                        <VIcon
                          v-if="item.status === 'Preferred'"
                          style="color: rgb(var(--v-theme-primary));"
                        >
                          iconamoon:certificate-badge-light
                        </VIcon>
                        <span
                          class="text-truncate"
                          :style="item.status === 'Preferred' ? 'color: rgb(var(--v-theme-primary));' : 'margin-left: 24px;'"
                          style="max-width: 150px;"
                        >
                          <span class="font-weight-bold">
                            {{ item.name }}
                          </span>
                        </span>
                        <span class="trending-name mt-1">
                          <RestaurantHeart
                            :restaurant-name="item.name"
                            :restaurant-market="item.market"
                          />
                        </span>
                      </div>
                    </div>
                  </VListItemTitle>
                  <VListItemSubtitle
                    :style="item.status === 'Preferred' ? 'margin-left: 24px; color: black;' : 'color: black; margin-left: 24px;'"
                  >
                    <div class="content">
                      <span
                        v-if="item.group"
                        class="trending-company text-xs"
                      >{{ item.group }}</span>
                      <span class="trending-company text-xs">{{ item.market }}</span>
                      <div class="d-flex align-center">
                        <div
                          class="d-flex cursor-pointer"
                          @click.stop="showRestaurantSpaces(item.market ?? item.raw.market, item.name ?? item.raw.name)"
                        >
                          <img
                            v-if="item?.eventSpaces?.length > 0"
                            :src="stanchion"
                            height="16"
                            class="align-self-center"
                          >
                          <span
                            v-if="item?.eventSpaces?.length > 0"
                            class="text-xs ml-1"
                          >
                            {{ item?.eventSpaces?.length }}
                          </span>
                        </div>
                        <VIcon
                          v-if="item?.showHappeningsIcon"
                          size="16"
                          class="align-self-center cursor-pointer"
                          @click.stop="showRestaurantHappenings(item.market ?? item.raw.market, item.name ?? item.raw.name)"
                        >
                          emojione-v1:lightning-mood
                        </VIcon>
                      </div>
                    </div>
                  </VListItemSubtitle>
                </div>
              </div>
              <template #append>
                <VBtn
                  size="small"
                  density="compact"
                  color="primary"
                  style="padding-left: 5px !important; padding-right: 5px !important;"
                  icon="mdi-plus"
                  @click.stop="newDraft(item.market, item.name);"
                />
              </template>
            </VListItem>
            <VDivider inset />
          </template>
        </VVirtualScroll>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style lang="scss" scoped>
.card-fixed-size {
  width: 300px; /* Fixed width */
  height: 270px; /* Fixed height */
  flex-shrink: 0; /* Prevents cards from shrinking */
}

.card-sm-size {
  width: 300px; /* Smaller width for small screens */
  height: 280px; /* Smaller height */
  flex-shrink: 0;
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none; /* Prevent dragging images */
}

.restaurant-image:hover.lower-on-hover {
  width: 90%;
  height: 90%;
}

.restaurant-image-holder {
  width: 100%;
  height: 50%;
}

.restaurant-image-holder--mobile {
  width: 4rem;
  height: 4rem;
}

.dark-text {
  color: black !important;
  font-weight: 400 !important;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
